interface PerformanceMetrics {
  name: string;
  callCount: number;
  totalTime: number;
  avgTime: number;
  errors: number;
  lastCall: number;
  startTime: number;
}

class PerformanceMonitor {
  private metrics = new Map<string, PerformanceMetrics>();
  private alertThresholds = {
    maxCallsPerMinute: 10,
    maxAvgTimeMs: 5000,
    maxErrorRate: 0.1
  };

  track<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const startTime = performance.now();
    
    return fn()
      .then((result) => {
        this.recordCall(name, performance.now() - startTime, false);
        return result;
      })
      .catch((error) => {
        this.recordCall(name, performance.now() - startTime, true);
        throw error;
      });
  }

  private recordCall(name: string, duration: number, isError: boolean) {
    const now = Date.now();
    const metric = this.metrics.get(name) || {
      name,
      callCount: 0,
      totalTime: 0,
      avgTime: 0,
      errors: 0,
      lastCall: now,
      startTime: now
    };

    metric.callCount++;
    metric.totalTime += duration;
    metric.avgTime = metric.totalTime / metric.callCount;
    metric.lastCall = now;
    
    if (isError) {
      metric.errors++;
    }

    this.metrics.set(name, metric);
    this.checkAlerts(metric);
  }

  private checkAlerts(metric: PerformanceMetrics) {
    const now = Date.now();
    const minutesSinceStart = (now - metric.startTime) / 60000;
    const callsPerMinute = metric.callCount / Math.max(minutesSinceStart, 1);
    const errorRate = metric.errors / metric.callCount;

    if (callsPerMinute > this.alertThresholds.maxCallsPerMinute) {
      console.warn(`🚨 High call rate for ${metric.name}: ${callsPerMinute.toFixed(1)} calls/min`);
    }

    if (metric.avgTime > this.alertThresholds.maxAvgTimeMs) {
      console.warn(`🚨 Slow performance for ${metric.name}: ${metric.avgTime.toFixed(0)}ms avg`);
    }

    if (errorRate > this.alertThresholds.maxErrorRate) {
      console.warn(`🚨 High error rate for ${metric.name}: ${(errorRate * 100).toFixed(1)}%`);
    }
  }

  getMetrics(): PerformanceMetrics[] {
    return Array.from(this.metrics.values());
  }

  getMetric(name: string): PerformanceMetrics | undefined {
    return this.metrics.get(name);
  }

  reset(name?: string) {
    if (name) {
      this.metrics.delete(name);
    } else {
      this.metrics.clear();
    }
  }

  logSummary() {
    const metrics = this.getMetrics();
    if (metrics.length === 0) {
      console.log('📊 No performance metrics available');
      return;
    }

    console.log('📊 Performance Summary:');
    metrics.forEach(metric => {
      const minutesSinceStart = (Date.now() - metric.startTime) / 60000;
      const callsPerMinute = metric.callCount / Math.max(minutesSinceStart, 1);
      const errorRate = (metric.errors / metric.callCount * 100);

      console.log(`  ${metric.name}:`);
      console.log(`    Calls: ${metric.callCount} (${callsPerMinute.toFixed(1)}/min)`);
      console.log(`    Avg Time: ${metric.avgTime.toFixed(0)}ms`);
      console.log(`    Error Rate: ${errorRate.toFixed(1)}%`);
      console.log(`    Last Call: ${Math.round((Date.now() - metric.lastCall) / 1000)}s ago`);
    });
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Export a hook for React components
export const usePerformanceMonitoring = () => {
  const trackFunction = <T>(name: string, fn: () => Promise<T>) => {
    return performanceMonitor.track(name, fn);
  };

  return {
    track: trackFunction,
    getMetrics: () => performanceMonitor.getMetrics(),
    logSummary: () => performanceMonitor.logSummary(),
    reset: (name?: string) => performanceMonitor.reset(name)
  };
};